<script setup lang="ts">
import { RouterLink } from "vue-router";

import { useUser } from "@/composables/api/useUser";

const { data: user } = useUser();
</script>

<template>
  <div
    v-if="user?.current_mechanic === 'galbani' && user?.active_hot_period_boost"
    class="flex w-full max-w-[342px] flex-col items-center pb-[40px] lg:grid lg:max-w-5xl lg:grid-cols-[389px_1fr] lg:items-center lg:pb-0"
  >
    <!-- Text + CTA on yellow background (on top in mobile, right in desktop) -->
    <div
      class="relative z-[1] flex w-full flex-col items-center gap-8 rounded-[40px] bg-[#FEB927] px-4 py-6 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.1)] lg:order-2 lg:ml-[-36px] lg:gap-6 lg:px-10 lg:py-10"
    >
      <div class="flex flex-col gap-8 text-center">
        <h3
          class="font-brygada text-2xl font-bold text-black lg:text-4xl"
          v-html="$t('gift.boost.title')"
        />

        <p class="text-base font-medium text-black lg:text-lg" v-html="$t('gift.boost.subtitle')" />
      </div>

      <RouterLink
        :to="{ name: 'receipts' }"
        class="flex items-center justify-center rounded-full bg-white px-[30px] py-3 text-lg font-medium text-[#214C9F] transition-opacity hover:opacity-90 lg:h-[60px] lg:text-xl"
      >
        {{ $t("gift.boost.cta") }}
      </RouterLink>
    </div>

    <!-- Packshots on blue background (below with overlap in mobile, left in desktop) -->
    <div
      class="relative -mt-[60px] h-[260px] w-[284px] overflow-hidden rounded-[40px] lg:order-1 lg:mt-0 lg:h-[350px] lg:w-[389px] lg:overflow-visible lg:rounded-none"
    >
      <!-- Blue background -->
      <div
        class="absolute inset-0 rounded-[40px] bg-[#B3DFFC] shadow-[0px_0px_20px_0px_rgba(0,0,0,0.1)]"
      />

      <!-- packshot-2 (Burrata) — top-left on mobile -->
      <img
        src="/images/packshots/packshot-2.png"
        alt=""
        class="pointer-events-none absolute top-[70px] left-[6.34%] w-[74px] object-cover drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:top-[60px] lg:left-[226px] lg:w-[102px]"
      />

      <!-- packshot-3 (Ricotta) — top-right on mobile -->
      <img
        src="/images/packshots/packshot-3.png"
        alt=""
        class="pointer-events-none absolute top-[75px] right-[7.04%] w-[80px] object-contain drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:top-[188px] lg:right-auto lg:left-[221px] lg:w-[102px]"
      />

      <!-- packshot-4 (Mascarpone) — center on mobile -->
      <img
        src="/images/packshots/packshot-4.png"
        alt=""
        class="pointer-events-none absolute top-[115px] left-1/2 w-[90px] -translate-x-1/2 object-contain drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:top-[135px] lg:left-[18px] lg:w-[102px] lg:translate-x-0"
      />

      <!-- packshot-1 (Mozzarella) — bottom-left on mobile -->
      <img
        src="/images/packshots/packshot-1.png"
        alt=""
        class="pointer-events-none absolute top-[170px] left-[8.8%] w-[77px] object-contain drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:top-[28px] lg:left-[92px] lg:w-[102px]"
      />

      <!-- packshot-5 (Mozzarella di Bufala) — bottom-right on mobile -->
      <img
        src="/images/packshots/packshot-5.png"
        alt=""
        class="pointer-events-none absolute top-[150px] left-[52%] w-[150px] object-contain drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:top-[210px] lg:left-[80px] lg:w-[160px]"
      />
    </div>
  </div>

  <!-- Lactel: Journée mondiale du lait boost banner -->
  <div
    v-else-if="user?.current_mechanic === 'lactel' && user?.active_hot_period_boost"
    class="flex w-full max-w-[342px] flex-col items-center pb-[40px] lg:grid lg:max-w-5xl lg:grid-cols-[388px_1fr] lg:items-center lg:pb-0"
  >
    <!-- Text + CTA on dark blue background (on top in mobile, right in desktop) -->
    <div
      class="bg-lactel-blue relative z-[1] flex w-full flex-col items-center gap-8 rounded-[40px] px-4 py-6 shadow-xl lg:order-2 lg:ml-[-36px] lg:gap-6 lg:px-10 lg:py-10"
    >
      <div class="flex flex-col gap-8 text-center">
        <h3
          class="font-mitr text-2xl font-bold text-white lg:text-[36px]"
          v-html="$t('gift.boost.title')"
        />

        <p class="text-base font-medium text-white lg:text-lg" v-html="$t('gift.boost.subtitle')" />
      </div>

      <RouterLink
        :to="{ name: 'receipts' }"
        class="text-galbani flex items-center justify-center rounded-full bg-white px-[30px] py-3 text-lg font-medium transition-opacity hover:opacity-90 lg:h-[60px] lg:text-xl"
      >
        {{ $t("gift.boost.cta") }}
      </RouterLink>
    </div>

    <!-- KV product photo on yellow background (below with overlap in mobile, left in desktop) -->
    <div
      class="relative -mt-[60px] h-[260px] w-[284px] overflow-hidden rounded-[40px] lg:order-1 lg:mt-0 lg:h-[350px] lg:w-[388px]"
    >
      <div class="bg-lactel-yellow absolute inset-0 hidden lg:block" />

      <picture>
        <source media="(min-width: 1024px)" srcset="/images/packshots/lactel-desktop.png" />

        <img
          src="/images/packshots/lactel-mobile.png"
          alt=""
          class="pointer-events-none absolute inset-0 h-full w-full object-contain"
        />
      </picture>
    </div>
  </div>
</template>
